// TESSERAX — RECONNAISSANCE DE « DÉGAGEMENT » (clutch clear). Cœur PUR : ni THREE ni DOM.
//
// Le jeu célébrait déjà le SAUVETAGE par Bascule (un repositionnement qui sort la pile du
// danger SANS effacer de ligne — cf. doBascule dans main.ts). Mais l'instant le plus dramatique
// d'un stacker — EFFACER des lignes alors que la pile FRÔLE le débordement et en RESSORTIR —
// ne recevait AUCUNE reconnaissance propre : il se fondait dans la célébration de clear générique
// (DOUBLE / TRIPLE / GRILLE…). On comble ce vide : un effacement de pièce posée qui ramène une
// pile en zone critique nettement plus bas est un « DÉGAGEMENT », le pendant du SAUVETAGE pour
// la pièce verrouillée — la remontada qui fait battre le cœur, le moment le plus partageable.
//
// Le seuil de « zone critique » est CALÉ sur l'état de danger VISIBLE (checkDanger dans main.ts :
// pile haute = vignette rouge + tension musicale, dès maxHauteur ≥ HEIGHT − DANGER_MARGIN). Du
// coup le DÉGAGEMENT ne se déclenche QUE quand un clear ÉTEINT ce danger visible avec une vraie
// chute — déclencheur net, rare et à fort signal (jamais du bruit) : on était dans le rouge, le
// clear nous en sort. Le palier `tier` pilote l'intensité du juice côté main.ts (flash/shake/son).
//
// PUR / déterministe : prédicat d'AFFICHAGE seul — aucun score, aucun RNG touché → déterminisme
// du défi du jour intact. Tolérant aux entrées non finies / négatives (→ aucun dégagement).

// Marge de danger : IDENTIQUE au seuil visuel de checkDanger (`maxH >= HEIGHT-3`) dans main.ts.
export const DANGER_MARGIN = 3;
// Chute minimale (en rangées) exigée : effacer UNE ligne qui gratte le sommet n'est pas un
// dégagement — il faut un vrai relâchement de pression pour mériter le bandeau.
export const MIN_RELIEF = 2;

export interface ClutchEval {
  clutch: boolean; // y a-t-il dégagement ?
  tier: number;    // 0 (aucun) puis 1..3 — intensité croissante (pilote le juice côté main.ts)
  drop: number;    // nombre de rangées dont la plus haute colonne a chuté grâce au clear
}

// `before`/`after` = hauteur de la PLUS HAUTE colonne AVANT (pièce posée incluse, lignes pas encore
// retirées) et APRÈS la résolution de l'effacement. `height` = HEIGHT du puits. Un DÉGAGEMENT exige
// trois conditions : (1) pile en zone critique avant — `before >= height - DANGER_MARGIN` (le rouge
// était allumé) ; (2) vrai relâchement — `drop >= MIN_RELIEF` ; (3) sortie SOUS la ligne de danger
// après — `after < dangerLine` (le rouge s'éteint). Renvoie un palier nul si l'une manque.
export function evalClutch(before: number, after: number, height: number): ClutchEval {
  const none: ClutchEval = { clutch: false, tier: 0, drop: 0 };
  if (!Number.isFinite(before) || !Number.isFinite(after) || !(height > 0)) return none;
  const dangerLine = height - DANGER_MARGIN;
  const drop = before - after;
  if (before < dangerLine || drop < MIN_RELIEF || after >= dangerLine) return none;
  // Palier : d'autant plus fort que la pile frôlait le débordement (proximité au sommet) ET que la
  // chute est ample. `proximity` = rangées AU-DESSUS de la ligne de danger au moment du clear.
  const proximity = before - dangerLine;
  let tier = 1;
  if (proximity >= 1 || drop >= 4) tier = 2;
  if (proximity >= 2 || drop >= 7) tier = 3; // dégagement in extremis (pile au ras du top) ou massif
  return { clutch: true, tier, drop };
}

// NOM ESCALADANT du dégagement. La mémoire du joueur retient un NOM, pas un numéro de palier
// (même philosophie que core/clears.lineClearName : « le Tetris du genre »). Plutôt qu'un unique
// « DÉGAGEMENT » pour tous les sauvetages, chaque palier porte SON cri de tension — du sursis
// modeste au sauvetage au ras du gouffre —, si bien que l'apogée GRANDIT avec la proximité du
// débordement. Vocabulaire de TENSION, VOLONTAIREMENT distinct du lexique cristallin des clears
// (DOUBLE…FAILLE) et des paliers Zone (TÉTRADE…OMNICUBE) pour ne pas brouiller la lecture.
// AFFICHAGE seul → main.ts mappe la clé i18n (`t(key, …, fallbackFr)`). Tolérant à tout entier.
export interface ClutchName { key: string; fallbackFr: string; }
export function clutchName(tier: number): ClutchName {
  const t = Number.isFinite(tier) ? Math.floor(tier) : 0;
  if (t <= 1) return { key: 'clutch.t1', fallbackFr: 'SURSIS' };
  if (t === 2) return { key: 'clutch.t2', fallbackFr: 'IN EXTREMIS' };
  return { key: 'clutch.t3', fallbackFr: 'AU BORD DU GOUFFRE' }; // ≥3 : le sauvetage maximal, au ras du top
}
